import { Container, Graphics, Text } from 'pixi.js';
import { FONT_MONO } from '../../theme/tokens';
import { FRONTIER_GLYPH } from './faceColour';

/**
 * Frontier `?` cardback glyph pool (ADR-0014 §1, consequence note on pooling).
 *
 * The naive approach — one `Text` per frontier hex — doesn't scale. Vision is
 * 19 hexes, so the realistic in-view frontier set is bounded at ~19; we
 * over-allocate a pool of 60 so a future vision-radius bump or zoom-out
 * surface still has headroom. Glyphs are created up front, parked invisible,
 * and re-positioned as the visible-frontier set shifts on each move.
 *
 * The pool deliberately knows nothing about pulse alpha — that's a frame-tick
 * concern owned by the renderer, which calls `forEachVisible` to apply the
 * shared alpha each tick.
 */

export const FRONTIER_GLYPH_POOL_CAPACITY = 60;

/** Pure-data position passed to `syncTo`. */
export interface GlyphPosition {
  readonly x: number;
  readonly y: number;
}

interface PoolSlot {
  readonly node: Container;
  key: string | null;
}

/**
 * Build a `?` glyph node: a `Text` slightly above hex centre with a thin
 * underline tick, both children of a `Container` so the pool can reposition
 * the whole group as a unit.
 *
 * `hexSize` is the renderer's `layout.size` (CSS-pixel hex radius); the
 * glyph and underline scale with it so the cardback reads at every zoom.
 */
function buildGlyphNode(hexSize: number): Container {
  const container = new Container();
  container.visible = false;
  // Eventless — frontier glyphs do not consume pointer events; the underlying
  // hex graphic still fires hover / click.
  container.eventMode = 'none';

  const fontSize = Math.round(hexSize * 0.9);
  const text = new Text({
    text: '?',
    style: {
      fontFamily: FONT_MONO,
      fontSize,
      fontWeight: 'bold',
      fill: FRONTIER_GLYPH,
      align: 'center',
    },
  });
  text.anchor.set(0.5, 0.5);
  // Lift the glyph slightly above centre so the underline tick can sit
  // beneath without crowding the hex outline (ADR-0014 §1).
  text.position.set(0, -hexSize * 0.08);

  const tick = new Graphics();
  const tickWidth = hexSize * 0.32;
  const tickY = hexSize * 0.3;
  tick
    .moveTo(-tickWidth / 2, tickY)
    .lineTo(tickWidth / 2, tickY)
    .stroke({ color: FRONTIER_GLYPH, width: Math.max(1, Math.round(hexSize * 0.04)) });

  container.addChild(text);
  container.addChild(tick);
  return container;
}

export class FrontierGlyphPool {
  private readonly slots: PoolSlot[] = [];
  private readonly assigned = new Map<string, PoolSlot>();
  private readonly parent: Container;
  private hoveredKey: string | null = null;

  constructor(parent: Container, hexSize: number, capacity: number = FRONTIER_GLYPH_POOL_CAPACITY) {
    this.parent = parent;
    for (let i = 0; i < capacity; i++) {
      const node = buildGlyphNode(hexSize);
      parent.addChild(node);
      this.slots.push({ node, key: null });
    }
  }

  /** Pool capacity (max glyphs the pool can have visible at once). */
  capacity(): number {
    return this.slots.length;
  }

  /** Number of glyphs currently visible. */
  visibleCount(): number {
    return this.assigned.size;
  }

  /**
   * Apply the visible-frontier set: release glyphs whose keys dropped out,
   * acquire glyphs for new keys, reposition existing assignments. Stable
   * under repeated calls with the same set (no churn).
   *
   * If the visible set exceeds `capacity()`, the surplus is silently dropped.
   * Caller is expected to bound `positions.size` to the vision disk; the
   * 60-slot pool is sized to never hit this branch with the slice-1 radius.
   */
  syncTo(positions: ReadonlyMap<string, GlyphPosition>): void {
    // Release first so freed slots are available for new acquisitions.
    for (const [key, slot] of this.assigned) {
      if (!positions.has(key)) {
        slot.key = null;
        slot.node.visible = false;
        this.assigned.delete(key);
        if (this.hoveredKey === key) this.hoveredKey = null;
      }
    }
    for (const [key, pos] of positions) {
      const existing = this.assigned.get(key);
      if (existing) {
        existing.node.position.set(pos.x, pos.y);
        continue;
      }
      const free = this.slots.find((s) => s.key === null);
      if (!free) return;
      free.key = key;
      free.node.position.set(pos.x, pos.y);
      free.node.visible = true;
      this.assigned.set(key, free);
    }
  }

  /** Apply `alpha` (typically the shared pulse value) to every visible glyph.
   *  The currently-hovered glyph (see `setHoveredKey`) holds at alpha 1.0
   *  regardless of the pulse value — ADR-0014 §5 hover brighten.
   */
  setAlpha(alpha: number): void {
    for (const [key, slot] of this.assigned) {
      slot.node.alpha = key === this.hoveredKey ? 1 : alpha;
    }
  }

  /**
   * Pin the named glyph to alpha 1.0, overriding the shared pulse — ADR-0014
   * §5 frontier hover brighten. Pass `null` to release. No-op when the key is
   * not currently assigned a glyph; the pin takes effect if/when it is and
   * `setAlpha` is called.
   */
  setHoveredKey(key: string | null): void {
    if (this.hoveredKey === key) return;
    this.hoveredKey = key;
    if (key !== null) {
      const slot = this.assigned.get(key);
      if (slot) slot.node.alpha = 1;
    }
  }

  /** Test seam — the key currently pinned to full alpha, or `null`. */
  getHoveredKey(): string | null {
    return this.hoveredKey;
  }

  /**
   * Re-add every pool slot to the parent container so the glyph layer floats
   * back to the top of the world container's child list. The renderer calls
   * this after `addHex` so newly-painted tiles don't occlude in-flight
   * cardbacks.
   */
  bringToFront(): void {
    for (const slot of this.slots) this.parent.addChild(slot.node);
  }

  destroy(): void {
    for (const slot of this.slots) {
      this.parent.removeChild(slot.node);
      slot.node.destroy({ children: true });
    }
    this.slots.length = 0;
    this.assigned.clear();
  }
}

/**
 * Pure assignment helper — tests use this without needing Pixi. Mirrors the
 * stable-under-repeat semantics of `FrontierGlyphPool.syncTo`.
 *
 * Returns the next `key → slot` map and a partition of slot indices into
 * `kept` / `released` / `acquired`. Surplus keys (beyond `capacity`) are
 * reported under `dropped`.
 */
export interface AssignmentResult {
  readonly next: ReadonlyMap<string, number>;
  readonly kept: ReadonlyArray<string>;
  readonly released: ReadonlyArray<string>;
  readonly acquired: ReadonlyArray<string>;
  readonly dropped: ReadonlyArray<string>;
}

export function assignFrontierGlyphs(
  previous: ReadonlyMap<string, number>,
  keys: ReadonlyArray<string>,
  capacity: number,
): AssignmentResult {
  const next = new Map<string, number>();
  const kept: string[] = [];
  const released: string[] = [];
  const acquired: string[] = [];
  const dropped: string[] = [];

  const desired = new Set(keys);

  const freeSlots: number[] = [];
  for (let i = 0; i < capacity; i++) freeSlots.push(i);

  for (const [key, slot] of previous) {
    if (desired.has(key)) {
      next.set(key, slot);
      kept.push(key);
      const idx = freeSlots.indexOf(slot);
      if (idx !== -1) freeSlots.splice(idx, 1);
    } else {
      released.push(key);
    }
  }

  for (const key of keys) {
    if (next.has(key)) continue;
    const slot = freeSlots.shift();
    if (slot === undefined) {
      dropped.push(key);
      continue;
    }
    next.set(key, slot);
    acquired.push(key);
  }

  return { next, kept, released, acquired, dropped };
}
